/**
 * Utilities for handling Frappe Link field search responses
 * 
 * Frappe's search_link API returns inconsistent formats:
 * - Direct array: [{value, description, label?}]
 * - Wrapped in .message: {message: [{value, description, label?}]}
 * - Wrapped in .data: {data: [{value, description, label?}]}
 * 
 * This module provides utilities to normalize and parse these responses.
 * 
 * Phase 2: Includes intelligent multi-word matching, fuzzy search, and ranking engine.
 */

export type LinkSearchResult = {
  value: string;
  description?: string;
  label?: string;
};

/**
 * Levenshtein distance cache for fuzzy matching
 * Cache limit: 1000 entries (evicts oldest when full)
 */
const levenshteinCache = new Map<string, number>();

/**
 * Clears the Levenshtein distance cache
 * Should be called when doctype changes (different vocabularies)
 */
export function clearLevenshteinCache(): void {
  levenshteinCache.clear();
}

/**
 * Computes Levenshtein distance between two strings
 * Uses memoization for performance
 */
function levenshteinDistance(str1: string, str2: string): number {
  const key = `${str1}|${str2}`;
  if (levenshteinCache.has(key)) {
    return levenshteinCache.get(key)!;
  }

  const len1 = str1.length;
  const len2 = str2.length;

  // Early exit for very different lengths
  if (Math.abs(len1 - len2) > 2) {
    return Infinity;
  }

  // Create matrix
  const matrix: number[][] = [];
  for (let i = 0; i <= len1; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= len2; j++) {
    matrix[0][j] = j;
  }

  // Fill matrix
  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1,     // deletion
          matrix[i][j - 1] + 1,     // insertion
          matrix[i - 1][j - 1] + 1  // substitution
        );
      }
    }
  }

  const distance = matrix[len1][len2];

  // Cache only small distances (≤ 2) to save memory
  if (distance <= 2) {
    levenshteinCache.set(key, distance);
    // Limit cache size - evict oldest when full
    if (levenshteinCache.size > 1000) {
      const firstKey = levenshteinCache.keys().next().value;
      levenshteinCache.delete(firstKey as any);
    }
  }

  return distance;
}

/**
 * Normalizes search_link API response to a consistent array format
 * Handles all Frappe response formats (array, .message, .data)
 * 
 * @param response - Raw response from frappe.desk.search.search_link
 * @returns Normalized array of link search results
 * 
 * @example
 * const response = await client.call("frappe.desk.search.search_link", {...});
 * const normalized = normalizeSearchLinkResponse(response);
 * // Returns: [{value: "Item", description: "Item", label?: "Item"}]
 */
export function normalizeSearchLinkResponse(response: any): LinkSearchResult[] {
  if (Array.isArray(response)) {
    return response;
  }
  
  if (response?.message && Array.isArray(response.message)) {
    return response.message;
  }
  
  if (response?.data && Array.isArray(response.data)) {
    return response.data;
  }
  
  // Fallback: return empty array if format is unexpected
  return [];
}

/**
 * Scores multi-word input against a label using token-by-token matching
 * Returns 0 for single-word input (delegates to other scoring functions)
 * 
 * Scoring rules:
 * - Exact word match: 100
 * - Word starts with token: 90
 * - Word contains token: 70
 * - Fuzzy match (distance ≤ 2): 60-80
 * - Cross-word matching: 60
 * - Weighted by token coverage
 * 
 * @param input - Search input (may contain multiple words)
 * @param label - Label to score against
 * @returns Score from 0-100, or 0 if single-word input
 */
export function scoreMultiWord(input: string, label: string): number {
  const inputLower = input.trim().toLowerCase();
  const labelLower = label.toLowerCase();

  if (inputLower.length === 0) return 0;

  // Split into tokens (words)
  const inputTokens = inputLower.split(/\s+/).filter(t => t.length > 0);
  const labelTokens = labelLower.split(/\s+/).filter(t => t.length > 0);

  // Return 0 for single-word input (delegates to other scoring)
  if (inputTokens.length <= 1) return 0;

  // Score each token
  let totalScore = 0;
  let matchedTokens = 0;

  for (const inputToken of inputTokens) {
    let bestMatch = 0;

    for (const labelToken of labelTokens) {
      // Exact word match: 100
      if (labelToken === inputToken) {
        bestMatch = Math.max(bestMatch, 100);
        continue;
      }

      // Word starts with token: 90
      if (labelToken.startsWith(inputToken)) {
        bestMatch = Math.max(bestMatch, 90);
        continue;
      }

      // Word contains token: 70
      if (labelToken.includes(inputToken)) {
        bestMatch = Math.max(bestMatch, 70);
        continue;
      }

      // Fuzzy match (distance ≤ 2): 60-80
      // Only fuzzy on words ≤ 20 chars to avoid performance issues
      if (inputToken.length <= 20 && labelToken.length <= 20) {
        const distance = levenshteinDistance(inputToken, labelToken);
        if (distance <= 2) {
          // Score: 80 - (distance * 10)
          bestMatch = Math.max(bestMatch, 80 - distance * 10);
        }
      }

      // Cross-word matching: 60 (token appears across word boundaries)
      if (labelLower.includes(inputToken)) {
        bestMatch = Math.max(bestMatch, 60);
      }
    }

    if (bestMatch > 0) {
      totalScore += bestMatch;
      matchedTokens++;
    }
  }

  if (matchedTokens === 0) return 0;

  // Weight by token coverage (percentage of tokens matched)
  const coverage = matchedTokens / inputTokens.length;
  const baseScore = totalScore / matchedTokens;

  return Math.round(baseScore * coverage);
}

/**
 * Scores input against label using fuzzy matching
 * 
 * Scoring rules:
 * - Exact match: 100
 * - Starts with: 90
 * - Contains: 70
 * - Fuzzy match (distance ≤ 2): 60-80
 * 
 * @param input - Search input
 * @param label - Label to score against
 * @returns Score from 0-100
 */
export function fuzzyScore(input: string, label: string): number {
  const inputLower = input.trim().toLowerCase();
  const labelLower = label.toLowerCase();

  // CRITICAL: Guard against extremely long inputs (prevent blocking main thread)
  if (inputLower.length > 50) return 0;

  if (inputLower.length === 0) return 0;

  // Exact match: 100
  if (labelLower === inputLower) return 100;

  // Starts with: 90
  if (labelLower.startsWith(inputLower)) return 90;

  // Contains: 70
  if (labelLower.includes(inputLower)) return 70;

  // Fuzzy match (distance ≤ 2): 60-80
  // Only fuzzy on strings ≤ 20 chars to avoid performance issues
  if (inputLower.length <= 20 && labelLower.length <= 20) {
    const distance = levenshteinDistance(inputLower, labelLower);
    if (distance <= 2) {
      // Score: 80 - (distance * 10)
      return 80 - distance * 10;
    }
  }

  // For multi-word inputs, try token-level fuzzy
  const inputTokens = inputLower.split(/\s+/).filter(t => t.length > 0);
  const labelTokens = labelLower.split(/\s+/).filter(t => t.length > 0);

  if (inputTokens.length > 1 && labelTokens.length > 1) {
    let bestTokenScore = 0;
    for (const inputToken of inputTokens) {
      if (inputToken.length > 20) continue; // Skip long tokens
      for (const labelToken of labelTokens) {
        if (labelToken.length > 20) continue; // Skip long tokens
        const distance = levenshteinDistance(inputToken, labelToken);
        if (distance <= 2) {
          bestTokenScore = Math.max(bestTokenScore, 80 - distance * 10);
        }
      }
    }
    if (bestTokenScore > 0) return bestTokenScore;
  }

  return 0;
}

/**
 * Ranks search results by relevance using multi-factor weighted scoring
 * 
 * Scoring factors (weighted):
 * 1. Exact match on label: 100
 * 2. Starts with label: 90
 * 3. Multi-word score: 0-80 (from scoreMultiWord)
 * 4. Fuzzy score: 0-80 (from fuzzyScore)
 * 5. Description match: 50
 * 6. Value match: 40
 * 7. Token coverage bonus: +10 per fully matched token
 * 8. Title field bonus: +5 (if label exists and differs from value)
 * 
 * @param input - Search input
 * @param results - Array of search results to rank
 * @returns Ranked array of results (highest score first)
 */
export function rankResults(input: string, results: LinkSearchResult[]): LinkSearchResult[] {
  const inputLower = input.trim().toLowerCase();
  const inputTokens = inputLower.split(/\s+/).filter(t => t.length > 0);

  if (inputLower.length === 0 || results.length === 0) {
    return results;
  }

  // Score each result
  const scoredResults = results.map((result) => {
    let score = 0;
    const label = (result.label || result.description || result.value || "").toLowerCase();
    const description = (result.description || "").toLowerCase();
    const value = (result.value || "").toLowerCase();

    // 1. Exact match on label: 100
    if (label === inputLower) {
      score = 100;
    }
    // 2. Starts with label: 90
    else if (label.startsWith(inputLower)) {
      score = 90;
    }
    // 3. Multi-word score: 0-80 (from scoreMultiWord)
    else {
      const multiWordScore = scoreMultiWord(inputLower, label);
      if (multiWordScore > 0) {
        score = multiWordScore;
      }
      // 4. Fuzzy score: 0-80 (from fuzzyScore)
      else {
        score = fuzzyScore(inputLower, label);
      }
    }

    // 5. Description match: 50 (only if label didn't match)
    if (score < 50 && description && description !== label) {
      if (description === inputLower) {
        score = Math.max(score, 50);
      } else if (description.startsWith(inputLower)) {
        score = Math.max(score, 45);
      } else if (description.includes(inputLower)) {
        score = Math.max(score, 40);
      }
    }

    // 6. Value match: 40 (only if label/description didn't match)
    if (score < 40 && value && value !== label && value !== description) {
      if (value === inputLower) {
        score = Math.max(score, 40);
      } else if (value.startsWith(inputLower)) {
        score = Math.max(score, 35);
      } else if (value.includes(inputLower)) {
        score = Math.max(score, 30);
      }
    }

    // 7. Token coverage bonus: +10 per fully matched token
    if (inputTokens.length > 1) {
      let matchedTokens = 0;
      for (const token of inputTokens) {
        if (label.includes(token) || description.includes(token) || value.includes(token)) {
          matchedTokens++;
        }
      }
      if (matchedTokens === inputTokens.length) {
        score += matchedTokens * 10;
      }
    }

    // 8. Title field bonus: +5 (if label exists and differs from value)
    if (result.label && result.label !== result.value) {
      score += 5;
    }

    return { result, score };
  });

  // Filter out zero-score results
  const filtered = scoredResults.filter(({ score }) => score > 0);

  // Sort by score (descending), then alphabetically by label (ascending) for tie-breaking
  filtered.sort((a, b) => {
    if (b.score !== a.score) {
      return b.score - a.score;
    }
    const labelA = (a.result.label || a.result.description || a.result.value || "").toLowerCase();
    const labelB = (b.result.label || b.result.description || b.result.value || "").toLowerCase();
    return labelA.localeCompare(labelB);
  });

  return filtered.map(({ result }) => result);
}

/**
 * Generic descriptions that should be ignored when parsing labels.
 * 
 * Some ERPNext doctypes return generic `description` values for all rows
 * (e.g., "All Item Groups", "All Customer Groups"). If we use these as labels,
 * all suggestions will look identical even though the underlying values differ.
 * 
 * Rule: Only add phrases that are known generic values from ERPNext core,
 * not arbitrary customer text.
 */
const GENERIC_DESCRIPTIONS_TO_IGNORE = new Set([
  "All Item Groups",
  "All Customer Groups",
  // Add more as we encounter them:
  // "All Suppliers",
  // "All Territories",
]);

/**
 * Extracts display label from Frappe link search result
 * 
 * Frappe's search_link API returns:
 * - value: the document name/id (what we use for selection)
 * - label: the display label (preferred when present, from title field)
 * - description: the formatted display string (may be generic like "All Item Groups")
 * 
 * This function implements the correct label extraction logic:
 * 1. Prefer label if exists (from title field when show_title_field_in_link is true)
 * 2. Use description if it's not a generic value
 * 3. Fallback to value (the actual document name - most reliable)
 * 
 * @param item - Link search result item from Frappe API
 * @param doctype - Optional doctype context for special handling (e.g., "Price List")
 * @returns Display label to show to user
 * 
 * @example
 * const item = {value: "Consumable", description: "All Item Groups"};
 * const label = parseLinkLabel(item);
 * // Returns: "Consumable" (uses value because description is generic)
 * 
 * @example
 * const item = {value: "Item", label: "Item Name", description: "Item"};
 * const label = parseLinkLabel(item);
 * // Returns: "Item Name" (prefers label)
 * 
 * @example
 * const item = {value: "Retail", description: "All Customer Groups"};
 * const label = parseLinkLabel(item);
 * // Returns: "Retail" (ignores generic description, uses value)
 */
export function parseLinkLabel(
  item: {
  value?: string;
  description?: string;
  label?: string;
  name?: string;
  },
  doctype?: string
): string {
  const itemValue = item.value || item.name || "";

  // Special handling for Price List: prefer value (price list name) over label (currency)
  // Price List has show_title_field_in_link=true with currency as title, but we want the price list name
  if (doctype === "Price List" && itemValue) {
    return itemValue; // Always use price list name, not currency code
  }

  // Special handling for Item Group (OPS-1535): Frappe's search_link returns
  // `description` populated with the parent group name (translated to the user's
  // language — e.g. "ทั้งหมด รายการ กลุ่ม" for "All Item Groups"). The English-only
  // GENERIC_DESCRIPTIONS_TO_IGNORE set misses translated values, so all children
  // of the same parent collapse onto one label. Item Group uses
  // autoname=field:item_group_name, so `value` IS the human-readable name — use it.
  if (doctype === "Item Group" && itemValue) {
    return itemValue;
  }
  
  // 1. Prefer label if exists (from title field when show_title_field_in_link is true)
  if (item.label) {
    return item.label;
  }
  
  // 2. Use description if it's not a generic value or redundant
  if (
    item.description &&
    !GENERIC_DESCRIPTIONS_TO_IGNORE.has(item.description) &&
    item.description !== itemValue
  ) {
    return item.description;
  }
  
  // 3. Fallback to value - this is the actual document name (most reliable)
  return itemValue;
}

/**
 * Processes and normalizes a search_link response, extracting unique results
 * with correct labels. Optionally applies intelligent ranking.
 * 
 * @param response - Raw response from frappe.desk.search.search_link
 * @param options - Optional configuration
 * @param options.searchInput - Search input for ranking (required if enableRanking is true)
 * @param options.enableRanking - Whether to apply intelligent ranking (default: false)
 * @returns Array of unique LinkSearchResult objects with correct labels, optionally ranked
 * 
 * @example
 * // Basic usage (backward compatible)
 * const response = await client.call("frappe.desk.search.search_link", {...});
 * const results = parseLinkSearchResponse(response);
 * 
 * @example
 * // With ranking enabled
 * const results = parseLinkSearchResponse(response, {
 *   searchInput: "T-shirt",
 *   enableRanking: true
 * });
 */
export function parseLinkSearchResponse(
  response: any,
  options?: {
    searchInput?: string;
    enableRanking?: boolean;
    doctype?: string; // Doctype context for label parsing (e.g., "Price List")
  }
): LinkSearchResult[] {
  // Normalize response format
  const normalized = normalizeSearchLinkResponse(response);
  
  // Process and deduplicate
  const uniqueResults = new Map<string, LinkSearchResult>();
  
  normalized.forEach((item: any) => {
    const itemValue = item.value || item.name || "";
    
    if (itemValue && itemValue.trim()) {
      // Use value as key to ensure uniqueness
      if (!uniqueResults.has(itemValue)) {
        const label = parseLinkLabel(item, options?.doctype);
        
        uniqueResults.set(itemValue, {
          value: itemValue, // Document name/id - used for selection
          label: label, // Display label - what user sees
          description: item.description || label,
        });
      }
    }
  });
  
  let results = Array.from(uniqueResults.values());

  // Apply ranking if enabled and searchInput is provided
  if (options?.enableRanking && options?.searchInput) {
    const searchInput = options.searchInput.trim();
    // Only rank if input is at least 2 characters
    if (searchInput.length >= 2) {
      results = rankResults(searchInput, results);
    }
  }

  return results;
}

